import { makeAutoObservable } from 'mobx';
import { State } from '../enum/state';
import http from '../services/http';
import { IResultDto } from '../models/ResultDto';
import req from '../services/req';
import { message } from 'antd';
import dayjs from "dayjs";


class PromosStore {
  data?: IObject[];
  item?: IObject;
  appliedPromoCode?: IObject | null;
  state: State = 'None';
  isLoading = false;
  isApplying = false;
  search = '';

  constructor() {
    makeAutoObservable(this);
  }

  get filteredResults() {
    if (!this.data || this.search === '') return this.data;
    return this.data.filter((e) =>
      `${e.code} ${e.name} ${e.description}`.toLowerCase().includes(this.search.toLowerCase())
    );
  }

  async findAll() {
    this.isLoading = true;
    try {
      const result = await http.get('promocodes');
      this.data = result.data.data;
    } finally {
      this.isLoading = false;
    }
  }

  async findApplied() {
    this.isLoading = true;
    try {
      const result = await req.get('promocode/get-applied');
      const value = result.data as IResultDto<IObject | null>;
      this.appliedPromoCode = value.data || null;
    } finally {
      this.isLoading = false;
    }
  }

  async apply(code: string) {
    this.isApplying = true;
    try {
      const result = await req.post('promocode/apply', { code });
      const value = result.data as IResultDto<IObject>;
      this.appliedPromoCode = value.data;
      message.success({ content: value.message });
      return value.data;
    } finally {
      this.isApplying = false;
    }
  }

  async remove(id: string) {
    this.isApplying = true;
    try {
      const result = await req.delete(`promocodes/${id}/remove`);
      const value = result.data as IResultDto<null>;
      this.appliedPromoCode = null;
      message.success({ content: value.message });
    } finally {
      this.isApplying = false;
    }
  }

  async onCreate(values: any) {
    this.isLoading = true;
    try {
      await http.post('promocodes', values);
      this.state = 'None';
    } finally {
      this.isLoading = false;
    }
  }

  async findOne(id: string) {
    this.isLoading = true;
    try {
      const result = await http.get(`promocodes/${id}`);
      this.item = result.data.data;
    } finally {
      this.isLoading = false;
    }
  }

  async onUpdate(values: any) {
    if (!this.item) return;
    this.isLoading = true;
    try {
      await http.put(`promocodes/${this.item.id}`, values);
      this.state = 'None';
    } finally {
      this.isLoading = false;
    }
  }

  async onRemove(id: string) {
    this.isLoading = true;
    try {
      await http.delete(`promocodes/${id}`);
      if (this.data) this.data = this.data.filter((e) => e.id !== id);
    } finally {
      this.isLoading = false;
    }
  }


  getPromoStatus(startsAt?: Date, expiresAt?: Date) {
    const now = dayjs();

    if (startsAt && now.isBefore(dayjs(startsAt))) {
      const daysUntil = dayjs(startsAt).diff(now, "days");
      return {
        text: `Начнется через ${daysUntil} дн.`,
        className: "pending",
        color: "blue",
        isActive: false,
      };
    }

    if (expiresAt && dayjs(expiresAt).isBefore(now)) {
      return {
        text: "Истек",
        className: "expired",
        color: "red",
        isActive: false,
      };
    }

    return {
      text: "Активен",
      className: "active",
      color: "green",
      isActive: true,
    };
  }

  getPromoStatusByItem(promo: IObject) {
    return this.getPromoStatus(promo.startsAt, promo.expiresAt);
  }

  generatePromoCode(length: number = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return Array.from({ length }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
  }
}


interface IObject {
  id: string;
  name: string;
  code: string;
  description: string;
  amount: number;
  type: { value: 'fixed' | 'percent'; title: string };
  startsAt: Date;
  expiresAt: Date;
  createdAt: Date;
}

const promosStore = new PromosStore();
export default promosStore;